import {
  IonButtons,
  IonModal,
  IonToolbar,
  IonHeader,
  IonContent,
  IonInput,
  IonText,
  IonAccordionGroup,
  IonAccordion,
  IonRange,
  isPlatform,
} from '@ionic/react';
import {
  ComponentProps,
  FunctionComponent,
  useRef,
  useState,
  useEffect,
} from 'react';
import Trans from 'util/Trans';
import { figures } from 'spinnaxBase/data/figures';
import DynamicButton from 'components/buttons/DynamicButton';
import './EditRunModal.css';
import { logger } from 'services/Logger';

// Cache keys für localStorage (außerhalb der Komponente definiert)
const CACHE_KEYS = {
  NAME: 'editRunModal_lastFigureName',
  ANGLE: 'editRunModal_lastAngle',
  BACKSIDE: 'editRunModal_lastBackside',
  SUCCESS: 'editRunModal_lastSuccess',
  STANCE: 'editRunModal_lastStance',
  ROTATION: 'editRunModal_lastRotation',
};

export interface EditRunModalProps extends ComponentProps<typeof IonModal> {
  onCommit: (updatedData: {
    name: string;
    angle: string;
    hasBackside: boolean;
    stance: string;
    rotation: string;
    success: boolean;
  }) => Promise<void>;
  currentRunData?: {
    name: string;
    angle: string;
    hasBackside: boolean;
    stance: string;
    rotation: string;
    success: boolean;
  };
}

const EditRunModal: FunctionComponent<EditRunModalProps> = ({
  onCommit,
  currentRunData: currentData,
  ...rest
}) => {
  const modalRef = useRef<HTMLIonModalElement>(null);

  // States für die Formularfelder
  const [editName, setEditName] = useState('');
  const [editAngle, setEditAngle] = useState<string>('0');
  const [editHasBackside, setEditHasBackside] = useState(false);
  const [editSuccess, setEditSucces] = useState(false);
  const [editStance, setEditStance] = useState<string>('R'); // 'R' für Regular, 'F' für Fakie, 'S' für Switch
  const [editRotation, setEditRotation] = useState<string>('0');
  const [showCustomInput, setShowCustomInput] = useState(false);

  // Hilfsfunktionen für lokalen Cache
  const saveToCache = (key: string, value: any) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      logger.warn('Failed to save to localStorage:', error);
    }
  };

  const loadFromCache = (key: string, defaultValue: any) => {
    try {
      const cached = localStorage.getItem(key);
      return cached ? JSON.parse(cached) : defaultValue;
    } catch (error) {
      logger.warn('Failed to load from localStorage:', error);
      return defaultValue;
    }
  };

  // Flag um zu verhindern, dass Cache beim ersten Load überschrieben wird
  const [isInitialized, setIsInitialized] = useState(false);

  // ✅ FIX: States mit aktuellen Daten initialisieren (verhindert infinite loops)
  useEffect(() => {
    // Use a ref to track if we've already initialized for this currentData
    const dataSignature = currentData ? JSON.stringify(currentData) : 'no-data';
    
    if (currentData) {
      // Bei vorhandenen Daten diese direkt verwenden (nicht Cache)
      setEditName(currentData.name || '');
      setEditAngle(currentData.angle || '0');
      setEditHasBackside(currentData.hasBackside || false);
      setEditSucces(currentData.success || false);
      setEditStance(currentData.stance || 'R');
      setEditRotation(currentData.rotation || '0');
    } else {
      // Keine aktuellen Daten vorhanden, Cache verwenden
      setEditName(loadFromCache(CACHE_KEYS.NAME, ''));
      setEditAngle(loadFromCache(CACHE_KEYS.ANGLE, '0'));
      setEditHasBackside(loadFromCache(CACHE_KEYS.BACKSIDE, false));
      setEditSucces(loadFromCache(CACHE_KEYS.SUCCESS, false));
      setEditStance(loadFromCache(CACHE_KEYS.STANCE, 'R'));
      setEditRotation(loadFromCache(CACHE_KEYS.ROTATION, '0'));
    }
    setIsInitialized(true);
  }, [currentData?.name, currentData?.angle, currentData?.hasBackside, currentData?.success, currentData?.stance, currentData?.rotation]); // ✅ FIX: Specific dependencies instead of whole object

  // Cache aktualisieren nur bei Modal-Schließung oder explizitem Speichern
  // Entfernt die useEffect Hooks, die bei jeder State-Änderung triggern
  const updateCache = () => {
    if (isInitialized) {
      saveToCache(CACHE_KEYS.NAME, editName);
      saveToCache(CACHE_KEYS.ANGLE, editAngle);
      saveToCache(CACHE_KEYS.BACKSIDE, editHasBackside);
      saveToCache(CACHE_KEYS.SUCCESS, editSuccess);
      saveToCache(CACHE_KEYS.STANCE, editStance);
      saveToCache(CACHE_KEYS.ROTATION, editRotation);
    }
  };

  const dismiss = () => {
    updateCache(); // ✅ Cache beim Schließen speichern
    modalRef.current?.dismiss();
  };

  const handleSave = async () => {
    updateCache(); // ✅ Cache beim Speichern speichern
    
    const updatedData = {
      name: editName,
      angle: editAngle,
      hasBackside: editHasBackside,
      stance: editStance,
      rotation: editRotation,
      success: editSuccess,
    };

    await onCommit(updatedData);
    dismiss();
  };

  // Hilfsfunktionen zum Konvertieren zwischen Zahl und Sides-String
  const sidesToNumber = (hasBackside: boolean): number => {
    if (hasBackside) return 1;
    return 0;
  };

  // Hilfsfunktionen zum Konvertieren zwischen Zahl und Success-String
  const successToNumber = (success: boolean): number => {
    if (success) return 1;
    return 0;
  };

  // Hilfsfunktionen zum Konvertieren zwischen Zahl und Stance-String
  const stanceToNumber = (stance: string): number => {
    if (stance === 'R') return 0;
    if (stance === 'G') return 1;
    if (stance === 'F') return 2;
    if (stance === 'S') return 3; // G für Goofy
    return 0;
  };

  const numberToStance = (value: number): string => {
    if (value === 0) return 'R';
    if (value === 1) return 'G';
    if (value === 2) return 'F';
    if (value === 3) return 'S';
    return 'R';
  };

  // Hilfsfunktionen zum Konvertieren zwischen Zahl und Angel-String
  const AngelToNumber = (angel: string): number => {
    if (angel === '0°') return 0;
    if (angel === '180°') return 1;
    if (angel === '360°') return 2;
    if (angel === '540°') return 3;
    if (angel === '720°') return 4;
    if (angel === '900°') return 5;
    if (angel === '1080°') return 6;
    return 0; // Standardwert
  };

  const numberToAngel = (value: number): string => {
    if (value === 0) return '0°';
    if (value === 1) return '180°';
    if (value === 2) return '360°';
    if (value === 3) return '540°';
    if (value === 4) return '720°';
    if (value === 5) return '900°';
    if (value === 6) return '1080°';
    return '0°'; // Standardwert
  };

  // Hilfsfunktionen zum Konvertieren zwischen Zahl und Rotation-String
  const RotationToNumber = (rotation: string): number => {
    if (rotation === '0') return 0;
    if (rotation === 'Double') return 1;
    if (rotation === 'Triple') return 2;
    if (rotation === 'Quad') return 3;
    return 0; // Standardwert
  };

  const numberToRotation = (value: number): string => {
    if (value === 0) return '0';
    if (value === 1) return 'Double';
    if (value === 2) return 'Triple';
    if (value === 3) return 'Quad';
    return '0'; // Standardwert
  };

  return (
    <IonModal {...rest} ref={modalRef} className="edit-modal">
      <IonHeader>
        {/* <div className="modal-image-svg">
          <img src={'/assets/images/Pic_Continent_Filter.svg'} />
        </div> */}
        <IonToolbar className="edit-modal-toptoolbar">
          <IonButtons slot="start">
            <DynamicButton
              text={<Trans i18nKey="OK" />}
              iconName="OKUpIcon"
              buttonType="icon-full"
              size="xlarge"
              fill="clear"
              onClick={handleSave}
            />
          </IonButtons>
          <IonButtons slot="end">
            <DynamicButton
              text={<Trans i18nKey="CANCEL" />}
              iconName="CancelUpIcon"
              buttonType="icon-full"
              size="xlarge"
              fill="clear"
              onClick={dismiss}
            />
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent
        style={{ '--background': 'transparent', '--border-colour': 0 }}
      >
        <div
          // className="modal"
          style={{
            padding: '0',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            alignItems: 'stretch',
            justifyContent: 'flex-start',
          }}
        >
          {/* TRICKNAME SECTION */}
          <IonAccordionGroup
            style={{
              marginLeft: '8px',
              marginRight: '8px',
              gap: 'px',
              flexDirection: 'column',
              backgroundColor: 'transparent',
              '--ion-item-background': 'transparent',
              '--ion-accordion-background': 'transparent',
            }}
            value="first"
          >
            <IonAccordion
              value="first"
              id="accordion"
              style={{ backgroundColor: 'transparent' }}
            >
              <div slot="content" style={{ backgroundColor: 'transparent' }}>
                <div
                  className="editor-container"
                  style={{ backgroundColor: 'transparent !important' }}
                >
                  <div
                    className="input-group"
                    style={{ backgroundColor: 'transparent !important' }}
                  >
                    <IonText className="field-label">
                      <Trans i18nKey="TRICK_NAME">Figure Name</Trans>
                    </IonText>
                    <select
                      value={editName}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        const value = e.target.value;
                        if (value === 'CUSTOM') {
                          setShowCustomInput(true);
                        } else {
                          setEditName(value);
                          setShowCustomInput(false);
                        }
                      }}
                      className="custom-select"
                    >
                      {figures.map((trick) => (
                        <option key={trick.value} value={trick.value}>
                          {trick.label}
                        </option>
                      ))}
                    </select>

                    {/* Zusätzliches Eingabefeld für eigene Runs */}
                    {showCustomInput && (
                      <IonInput
                        value={editName}
                        onIonChange={(e) => setEditName(e.detail.value!)}
                        className="custom-input"
                        placeholder="Gib eine custom Figure ein"
                      />
                    )}
                  </div>
                </div>
              </div>
            </IonAccordion>
          </IonAccordionGroup>

          {/* STANCE SECTION */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0px',
              margin: '0 16px',
              backgroundColor: 'transparent',
              transform: 'translateY(-20px)', // Leicht nach oben verschoben
            }}
          >
            {/* STANCE Label */}
            <div
              style={{
                color: 'white',
                fontSize: '16px',
                fontWeight: 'bold',
                transform: 'translateY(20px)', // Leicht nach unten verschoben
              }}
            >
              <Trans i18nKey="STANCE" defaults="Stance" />
            </div>

            {/* Range Container */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                // backgroundColor: 'blue ',
              }}
            >
              {/* Raute Min-Wert */}
              <div
                style={{
                  width: '30px',
                  height: '30px',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  transform: 'rotate(45deg)',
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '20px',
                  color: '#222',
                  position: 'relative',
                  marginTop: isPlatform('ios') ? '20px' : '28px', // Plattformabhängige Anpassung
                }}
              >
                {/* Min-Wert */}
                <span
                  style={{
                    transform: 'rotate(-45deg)',
                    display: 'block',
                    width: '100%',
                    textAlign: 'center',
                  }}
                >
                  {/* {rangeValues.lower} */}
                </span>
              </div>
              <IonRange
                aria-label="Range with ticks"
                className="diamond-knobs"
                ticks={true}
                snaps={true}
                min={0}
                max={3}
                value={stanceToNumber(editStance)} // Konvertiert den String-Wert in eine Zahl
                pin={true}
                pinFormatter={(value: number) => {
                  if (value === 0) return 'R';
                  if (value === 1) return 'G';
                  if (value === 2) return 'F';
                  if (value === 3) return 'S';
                  return value.toString();
                }}
                onIonChange={(e) => {
                  const val = e.detail.value as number;
                  setEditStance(numberToStance(val)); // Konvertiert die Zahl zurück in einen String
                }}
              />

              {/* Raute Max-Wert */}
              <div
                style={{
                  width: '30px',
                  height: '30px',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  transform: 'rotate(45deg)',
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '20px',
                  color: '#222',
                  position: 'relative',
                  marginTop: isPlatform('ios') ? '20px' : '28px', // Plattformabhängige Anpassung
                }}
              >
                {/* Max-Wert */}
                <span
                  style={{
                    transform: 'rotate(-45deg)',
                    display: 'block',
                    width: '100%',
                    textAlign: 'center',
                    // marginTop: '15px', // Text 15px nach unten verschieben
                  }}
                >
                  {/* {rangeValues.upper} */}
                </span>
              </div>
            </div>
            {/* Buchstaben unter der IonRange */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '0px',
                color: 'white',
                fontSize: '14px',
                fontWeight: 'bold',
                marginLeft: '12px',
                marginRight: '12px',
              }}
            >
              <span>Regular</span>
              <span>Goofy</span>
              <span>Fakie</span>
              <span>Switch</span>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'row', gap: '0px' }}>
            {/* SIDES SECTION */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0px',
                margin: '0 16px',
                backgroundColor: 'transparent',
                transform: 'translateY(-20px)', // Leicht nach oben verschoben
              }}
            >
              {/* SIDE Label */}
              <div
                style={{
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  transform: 'translateY(20px)', // Leicht nach unten verschoben
                }}
              >
                <Trans i18nKey="SIDES" defaults="Sides" />
              </div>

              {/* Range Container */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '150px',
                  // backgroundColor: 'blue ',
                }}
              >
                {/* Raute Min-Wert */}
                <div
                  style={{
                    width: '30px',
                    height: '30px',
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    transform: 'rotate(45deg)',
                    userSelect: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '20px',
                    color: '#222',
                    position: 'relative',
                    marginTop: isPlatform('ios') ? '20px' : '28px', // Plattformabhängige Anpassung
                  }}
                >
                  {/* Min-Wert */}
                  <span
                    style={{
                      transform: 'rotate(-45deg)',
                      display: 'block',
                      width: '100%',
                      textAlign: 'center',
                    }}
                  >
                    {/* {rangeValues.lower} */}
                  </span>
                </div>
                <IonRange
                  aria-label="Range with ticks"
                  className="diamond-knobs"
                  ticks={true}
                  snaps={true}
                  min={0}
                  max={1}
                  value={sidesToNumber(editHasBackside) ? 1 : 0} // Konvertiert den Boolean-Wert in eine Zahl
                  pin={true}
                  pinFormatter={(value: number) => {
                    if (value === 0) return 'F';
                    if (value === 1) return 'B';
                    return value.toString();
                  }}
                  onIonChange={(e) => {
                    const val = e.detail.value as number;
                    setEditHasBackside(val === 1); // Setzt den Boolean-Wert basierend auf der Zahl
                  }}
                />

                {/* Raute Max-Wert */}
                <div
                  style={{
                    width: '30px',
                    height: '30px',
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    transform: 'rotate(45deg)',
                    userSelect: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '20px',
                    color: '#222',
                    position: 'relative',
                    marginTop: isPlatform('ios') ? '20px' : '28px', // Plattformabhängige Anpassung
                  }}
                >
                  {/* Max-Wert */}
                  <span
                    style={{
                      transform: 'rotate(-45deg)',
                      display: 'block',
                      width: '100%',
                      textAlign: 'center',
                      // marginTop: '15px', // Text 15px nach unten verschieben
                    }}
                  >
                    {/* {rangeValues.upper} */}
                  </span>
                </div>
              </div>
              {/* Buchstaben unter der IonRange */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '0px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  marginLeft: '12px',
                  marginRight: '20px',
                  width: '130px', // Breite anpassen, um die Buchstaben zu zentrieren
                }}
              >
                <span>Frontside</span>
                <span>Backside</span>
              </div>
            </div>

            {/* SUCCESS SECTION */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0px',
                margin: '0 16px',
                backgroundColor: 'transparent',
                transform: 'translateY(-20px)', // Leicht nach oben verschoben
              }}
            >
              {/* SIDE Label */}
              <div
                style={{
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  transform: 'translateY(20px)', // Leicht nach unten verschoben
                }}
              >
                <Trans i18nKey="SUCCESS" defaults="Success" />
              </div>

              {/* Range Container */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '150px',
                  // backgroundColor: 'blue ',
                }}
              >
                {/* Raute Min-Wert */}
                <div
                  style={{
                    width: '30px',
                    height: '30px',
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    transform: 'rotate(45deg)',
                    userSelect: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '20px',
                    color: '#222',
                    position: 'relative',
                    marginTop: isPlatform('ios') ? '20px' : '28px', // Plattformabhängige Anpassung
                  }}
                >
                  {/* Min-Wert */}
                  <span
                    style={{
                      transform: 'rotate(-45deg)',
                      display: 'block',
                      width: '100%',
                      textAlign: 'center',
                    }}
                  >
                    {/* {rangeValues.lower} */}
                  </span>
                </div>
                <IonRange
                  aria-label="Range with ticks"
                  className="diamond-knobs"
                  ticks={true}
                  snaps={true}
                  min={0}
                  max={1}
                  value={successToNumber(editSuccess) ? 1 : 0} // Konvertiert den Boolean-Wert in eine Zahl
                  pin={true}
                  pinFormatter={(value: number) => {
                    if (value === 0) return 'L';
                    if (value === 1) return 'B';
                    return value.toString();
                  }}
                  onIonChange={(e) => {
                    const val = e.detail.value as number;
                    setEditSucces(val === 1); // Setzt den Boolean-Wert basierend auf der Zahl
                  }}
                />

                {/* Raute Max-Wert */}
                <div
                  style={{
                    width: '30px',
                    height: '30px',
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    transform: 'rotate(45deg)',
                    userSelect: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '20px',
                    color: '#222',
                    position: 'relative',
                    marginTop: isPlatform('ios') ? '20px' : '28px', // Plattformabhängige Anpassung
                  }}
                >
                  {/* Max-Wert */}
                  <span
                    style={{
                      transform: 'rotate(-45deg)',
                      display: 'block',
                      width: '100%',
                      textAlign: 'center',
                      // marginTop: '15px', // Text 15px nach unten verschieben
                    }}
                  >
                    {/* {rangeValues.upper} */}
                  </span>
                </div>
              </div>
              {/* Buchstaben unter der IonRange */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '0px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  marginLeft: '12px',
                  marginRight: '20px',
                  width: '130px', // Breite anpassen, um die Buchstaben zu zentrieren
                }}
              >
                <span>Bailed</span>
                <span>Landed</span>
              </div>
            </div>
          </div>

          {/* ANGLE SECTION */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0px',
              margin: '0 16px',
              backgroundColor: 'transparent',
              transform: 'translateY(-20px)', // Leicht nach oben verschoben
              // backgroundColor: 'red',
            }}
          >
            {/* ANGLE Label */}
            <div
              style={{
                color: 'white',
                fontSize: '16px',
                fontWeight: 'bold',
                transform: 'translateY(20px)', // Leicht nach unten verschoben
              }}
            >
              <Trans i18nKey="ANGLE" defaults="Winkel" />
            </div>

            {/* Range Container */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                // backgroundColor: 'blue ',
              }}
            >
              {/* Raute Min-Wert */}
              <div
                style={{
                  width: '30px',
                  height: '30px',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  transform: 'rotate(45deg)',
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '20px',
                  color: '#222',
                  position: 'relative',
                  marginTop: isPlatform('ios') ? '20px' : '28px', // Plattformabhängige Anpassung
                }}
              >
                {/* Min-Wert */}
                <span
                  style={{
                    transform: 'rotate(-45deg)',
                    display: 'block',
                    width: '100%',
                    textAlign: 'center',
                  }}
                >
                  {/* {rangeValues.lower} */}
                </span>
              </div>
              <IonRange
                aria-label="Range with ticks"
                className="diamond-knobs"
                ticks={true}
                snaps={true}
                min={0}
                max={6} // Maximalwert für Winkel
                value={AngelToNumber(editAngle)} // Konvertiert den String-Wert in eine Zahl
                pin={true}
                pinFormatter={(value: number) => {
                  if (value === 0) return '0°';
                  if (value === 1) return '180°';
                  if (value === 2) return '360°';
                  if (value === 3) return '540°';
                  if (value === 4) return '720°';
                  if (value === 5) return '900°';
                  if (value === 6) return '1080°';

                  return value.toString();
                }}
                onIonChange={(e) => {
                  const val = e.detail.value as number;
                  setEditAngle(numberToAngel(val)); // Konvertiert die Zahl zurück in einen String
                }}
              />

              {/* Raute Max-Wert */}
              <div
                style={{
                  width: '30px',
                  height: '30px',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  transform: 'rotate(45deg)',
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '20px',
                  color: '#222',
                  position: 'relative',
                  marginTop: isPlatform('ios') ? '20px' : '28px', // Plattformabhängige Anpassung
                }}
              >
                {/* Max-Wert */}
                <span
                  style={{
                    transform: 'rotate(-45deg)',
                    display: 'block',
                    width: '100%',
                    textAlign: 'center',
                    // marginTop: '15px', // Text 15px nach unten verschieben
                  }}
                >
                  {/* {rangeValues.upper} */}
                </span>
              </div>
            </div>
            {/* Buchstaben unter der IonRange */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '0px',
                color: 'white',
                fontSize: '14px',
                fontWeight: 'bold',
                marginLeft: '30px',
                marginRight: '10px',
              }}
            >
              <span>0°</span>
              <span>180°</span>
              <span>360°</span>
              <span>540°</span>
              <span>720°</span>
              <span>900°</span>
              <span>1080°</span>
            </div>
          </div>

          {/* ROTATION SECTION */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0px',
              margin: '0 16px',
              backgroundColor: 'transparent',
              transform: 'translateY(-20px)', // Leicht nach oben verschoben
              // backgroundColor: 'red',
            }}
          >
            {/* ROTATION Label */}
            <div
              style={{
                color: 'white',
                fontSize: '16px',
                fontWeight: 'bold',
                transform: 'translateY(20px)', // Leicht nach unten verschoben
              }}
            >
              <Trans i18nKey="ROTATION" defaults="Rotation" />
            </div>

            {/* Range Container */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                // backgroundColor: 'blue ',
              }}
            >
              {/* Raute Min-Wert */}
              <div
                style={{
                  width: '30px',
                  height: '30px',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  transform: 'rotate(45deg)',
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '20px',
                  color: '#222',
                  position: 'relative',
                  marginTop: isPlatform('ios') ? '20px' : '28px', // Plattformabhängige Anpassung
                }}
              >
                {/* Min-Wert */}
                <span
                  style={{
                    transform: 'rotate(-45deg)',
                    display: 'block',
                    width: '100%',
                    textAlign: 'center',
                  }}
                >
                  {/* {rangeValues.lower} */}
                </span>
              </div>
              <IonRange
                aria-label="Range with rotation"
                className="diamond-knobs"
                ticks={true}
                snaps={true}
                min={0}
                max={3} // Maximalwert für Winkel
                value={RotationToNumber(editRotation)} // Konvertiert den String-Wert in eine Zahl
                pin={true}
                pinFormatter={(value: number) => {
                  if (value === 0) return '0';
                  if (value === 1) return 'Double';
                  if (value === 2) return 'Triple';
                  if (value === 3) return 'Quad';

                  return value.toString();
                }}
                onIonChange={(e) => {
                  const val = e.detail.value as number;
                  setEditRotation(numberToRotation(val)); // Konvertiert die Zahl zurück in einen String
                }}
              />

              {/* Raute Max-Wert */}
              <div
                style={{
                  width: '30px',
                  height: '30px',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  transform: 'rotate(45deg)',
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '20px',
                  color: '#222',
                  position: 'relative',
                  marginTop: isPlatform('ios') ? '20px' : '28px', // Plattformabhängige Anpassung
                }}
              >
                {/* Max-Wert */}
                <span
                  style={{
                    transform: 'rotate(-45deg)',
                    display: 'block',
                    width: '100%',
                    textAlign: 'center',
                    // marginTop: '15px', // Text 15px nach unten verschieben
                  }}
                >
                  {/* {rangeValues.upper} */}
                </span>
              </div>
            </div>
            {/* Buchstaben unter der IonRange */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '0px',
                color: 'white',
                fontSize: '14px',
                fontWeight: 'bold',
                marginLeft: '30px',
                marginRight: '10px',
              }}
            >
              <span>0</span>
              <span>Double</span>
              <span>Triple</span>
              <span>Quad</span>
            </div>
          </div>

          {/* SAVE BUTTON */}
          {/* <IonButton
                    expand="block"
                    className="save-button"
                    color="primary"
                    onClick={handleSave}
                  >
                    <Trans i18nKey="SAVE">Speichern</Trans>
                  </IonButton> */}
        </div>
      </IonContent>
    </IonModal>
  );
};

export default EditRunModal;
